import { Pencil, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'

import { useDeleteRecipe, useRecipe, useUnits } from '@/api/hooks'
import { CommentThread } from '@/components/CommentThread'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { useWakeLock } from '@/hooks/useWakeLock'
import { fr } from '@/i18n/fr'
import { formatMeasure } from '@/lib/format'

export function RecipeDetailPage() {
  const { id } = useParams<{ id: string }>()
  const recipeId = Number(id)
  const { data: recipe, isPending } = useRecipe(recipeId)
  const { data: units } = useUnits()
  const deleteRecipe = useDeleteRecipe()
  const navigate = useNavigate()
  const location = useLocation()
  const [confirmOpen, setConfirmOpen] = useState(false)
  // Set by the editor when the recipe saved but its photo did not (F-20). Dismissible, and
  // gone on the next navigation — it describes what just happened, not a state of the recipe.
  const [photoFailed, setPhotoFailed] = useState(
    Boolean((location.state as { photoFailed?: boolean } | null)?.photoFailed),
  )

  useWakeLock(Boolean(recipe))

  if (isPending) {
    return <p className="text-stone-500">{fr.common.loading}</p>
  }

  if (!recipe) {
    return <p className="text-stone-500">{fr.recipeDetail.notFound}</p>
  }

  function handleDelete() {
    deleteRecipe.mutate(recipeId, { onSuccess: () => void navigate('/', { replace: true }) })
  }

  return (
    <div className="mx-auto max-w-3xl">
      <Link to="/" className="text-sm text-stone-500 hover:underline">
        ← {fr.recipeDetail.back}
      </Link>

      {photoFailed && (
        <div
          role="alert"
          className="mt-4 flex items-start justify-between gap-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-950 dark:text-amber-200"
        >
          <p>{fr.recipeEdit.photo.failedAfterSave}</p>
          <button
            type="button"
            onClick={() => setPhotoFailed(false)}
            aria-label={fr.common.close}
            className="shrink-0 font-medium underline"
          >
            {fr.common.close}
          </button>
        </div>
      )}

      {recipe.photo && (
        <img
          src={recipe.photo}
          alt=""
          className="mt-4 aspect-[16/9] w-full rounded-2xl object-cover"
        />
      )}

      <div className="mt-4 flex items-start justify-between gap-4">
        <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">{recipe.title}</h1>
        {/* F-7: the server decides, and says so on the payload (NFR-37). Re-deriving the
            rule from `created_by` here is what makes the button and the API disagree. */}
        {recipe.can_edit && (
          <div className="flex shrink-0 gap-2">
            <Button asChild variant="outline" size="icon">
              <Link to={`/recipes/${recipe.id}/edit`} aria-label={fr.common.edit}>
                <Pencil className="h-4 w-4" />
              </Link>
            </Button>
            <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="icon" aria-label={fr.common.delete}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{fr.recipeDetail.deleteConfirm.title}</DialogTitle>
                  <DialogDescription>{fr.recipeDetail.deleteConfirm.description}</DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setConfirmOpen(false)}>
                    {fr.common.cancel}
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handleDelete}
                    disabled={deleteRecipe.isPending}
                  >
                    {fr.recipeDetail.deleteConfirm.confirm}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      {/* Null for a seeded recipe, whose author was never recorded — crediting nobody is
          worse than crediting no one. The server sends a name, never an address (NFR-39). */}
      {recipe.created_by && (
        <p className="mt-1 text-stone-500">{fr.recipeDetail.addedBy(recipe.created_by)}</p>
      )}

      {(recipe.servings != null ||
        recipe.prep_time_min != null ||
        recipe.cook_time_min != null ||
        recipe.total_time_min != null) && (
        <dl className="mt-4 grid grid-cols-2 gap-4 rounded-2xl border border-stone-200 p-4 text-sm dark:border-stone-800 sm:grid-cols-4">
          {recipe.servings != null && (
            <Fact label={fr.recipeDetail.servings} value={String(recipe.servings)} />
          )}
          {/* The same formatter as the card, deliberately: a recipe that says `3 h 30` in
              the grid and `210 min` once opened is one recipe telling you two things. */}
          {recipe.prep_time_min != null && (
            <Fact label={fr.recipeDetail.prepTime} value={fr.common.duration(recipe.prep_time_min)} />
          )}
          {recipe.cook_time_min != null && (
            <Fact label={fr.recipeDetail.cookTime} value={fr.common.duration(recipe.cook_time_min)} />
          )}
          {recipe.total_time_min != null && (
            <Fact label={fr.recipeDetail.totalTime} value={fr.common.duration(recipe.total_time_min)} />
          )}
        </dl>
      )}

      {recipe.description && <p className="mt-6 text-lg leading-relaxed">{recipe.description}</p>}

      {recipe.tags && recipe.tags.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-1.5">
          {recipe.tags.map((tag) => (
            <span
              key={tag}
              className="rounded-full bg-stone-100 px-2.5 py-1 text-sm text-stone-600 dark:bg-stone-800 dark:text-stone-400"
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      {recipe.ingredients && recipe.ingredients.length > 0 && (
        <section className="mt-8">
          <h2 className="text-xl font-semibold">{fr.recipeDetail.ingredients}</h2>
          <ul className="mt-3 flex flex-col gap-2 text-lg">
            {recipe.ingredients.map((ingredient, index) => (
              <li key={index} className="flex gap-2">
                <span className="text-stone-400" aria-hidden>
                  •
                </span>
                <span>
                  {/* NFR-36 rounds the stored Decimal, NFR-38 drops "pièce" — so this
                      reads "1,5 kg pommes" and "3 oignons", not "1.500 kg" and
                      "3 pièces oignons". */}
                  {formatMeasure(ingredient.quantity, ingredient.unit, units)}{' '}
                  {ingredient.name}
                  {ingredient.is_optional && (
                    <span className="text-stone-400"> ({fr.common.optional})</span>
                  )}
                  {ingredient.note && <span className="text-stone-500"> — {ingredient.note}</span>}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {recipe.steps && recipe.steps.length > 0 && (
        <section className="mt-8">
          <h2 className="text-xl font-semibold">{fr.recipeDetail.steps}</h2>
          <ol className="mt-3 flex flex-col gap-4 text-lg leading-relaxed">
            {recipe.steps.map((step, index) => (
              <li key={index} className="flex gap-3">
                <span className="font-semibold text-stone-400">{index + 1}.</span>
                <span>{step.text}</span>
              </li>
            ))}
          </ol>
        </section>
      )}

      {(recipe.source || recipe.notes) && (
        <section className="mt-8 rounded-2xl border border-dashed border-stone-300 p-4 text-sm text-stone-600 dark:border-stone-700 dark:text-stone-400">
          {recipe.source && (
            <p>
              <span className="font-medium">{fr.recipeDetail.source} : </span>
              {recipe.source}
            </p>
          )}
          {recipe.notes && (
            <p className="mt-1">
              <span className="font-medium">{fr.recipeDetail.notes} : </span>
              {recipe.notes}
            </p>
          )}
        </section>
      )}

      {/* Last, and available to everyone — including whoever has no edit button above
          (F-16). `notes` is the owner's aside; this is everybody else's. */}
      <CommentThread recipeId={recipe.id} />
    </div>
  )
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-stone-500">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  )
}
